import React, {useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import Styles from './styles.module.scss';
import {useDispatch, useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {toast} from "react-toastify";
import {
    getReferralsMain,
    setMatrixBuyModalStatus,
    setMatrixSellModalStatus
} from "../../store/cabinet/action";
import {formatNumber, SITE_URL, WHITEPAPER_URL} from "../cabinetShell/config";
import RoundCard from "../_UI/roundCard";
import BrushButton from "../_UI/brushButton";
import CommunityPromo from "../_UI/communityPromo";

import heroArt from '../../theme/assets/redesign/hero.webp';
import heroArtMobile from '../../theme/assets/redesign/hero.webp';
import whitepaperBtn from '../../theme/assets/redesign/btn_whitepaper.svg';
import openSiteBtn from '../../theme/assets/redesign/btn_open_site.svg';
import documentIcon from '../../theme/assets/redesign/icon_document.svg';
import externalIcon from '../../theme/assets/redesign/icon_external.svg';
import prevBtn from '../../theme/assets/redesign/btn_prev.svg';
import nextBtn from '../../theme/assets/redesign/btn_next.svg';
import prevBtnMob from '../../theme/assets/redesign/m_btn_prev.svg';
import nextBtnMob from '../../theme/assets/redesign/m_btn_next.svg';
import priceTagIcon from '../../theme/assets/redesign/icon_price_tag.svg';
import coinsIcon from '../../theme/assets/redesign/icon_coins.svg';
import ticketIcon from '../../theme/assets/redesign/icon_ticket.svg';
import usersIcon from '../../theme/assets/redesign/icon_users_blue.svg';
import chevronIcon from '../../theme/assets/redesign/chevron.svg';
import chevronIconMob from '../../theme/assets/redesign/m_chevron.svg';


// статус круга из API -> состояние карточки из макета
const STATE_BY_STATUS = {
    active: 'active',
    closed: 'completed',
    inactive: 'upcoming',
};

const usd = (value) => `$${formatNumber(value)}`;

const StatCard = ({icon, label, value, unit, unitAccent, secondary, onClick}) => (
    <div className={Styles.statCard} onClick={onClick}>
        <img className={Styles.statIcon} src={icon} alt=""/>
        <div className={Styles.statText}>
            <div className={Styles.statLabel}>{label}</div>
            <div className={Styles.statValue}>
                <span>{value}</span>
                {unit ? <span className={unitAccent ? Styles.red : ''}>{unit}</span> : null}
                {secondary ? <span className={Styles.statSecondary}>{secondary}</span> : null}
            </div>
        </div>
        <img className={`${Styles.statChevron} ${Styles.desktopOnly}`} src={chevronIcon} alt="" width="20" height="20"/>
        <img className={`${Styles.statChevron} ${Styles.mobileOnly}`} src={chevronIconMob} alt="" width="16" height="16"/>
    </div>
);


const CabinetMatrixMain = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {t} = useTranslation();

    const matrix = useSelector(store => store.cabinet.matrix);
    const userData = useSelector(store => store.cabinet.userData);
    const referralsMain = useSelector(store => store.cabinet.referralsMain);

    const laps = useMemo(() => (
        Array.isArray(matrix) ? [...matrix].sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0)) : []
    ), [matrix]);

    // круг, на котором сейчас идут продажи (или последний закрытый) — на него завязаны Buy/Sell
    const focusIndex = useMemo(() => {
        if (!laps.length) return -1;
        const activeIndex = laps.findIndex(x => x?.status === 'active');
        if (activeIndex !== -1) return activeIndex;
        for (let i = laps.length - 1; i >= 0; i--) if (laps[i]?.status === 'closed') return i;
        return 0;
    }, [laps]);

    const currentLap = focusIndex !== -1 ? laps[focusIndex] : null;
    const currentLapActive = currentLap?.status === 'active';

    // выбранный в карусели круг
    const [selected, setSelected] = useState(0);
    useEffect(() => {
        if (focusIndex !== -1) setSelected(focusIndex);
    }, [focusIndex]);

    useEffect(() => {
        dispatch(getReferralsMain());
    }, [dispatch]);

    const canBuy = (userData?.user?.available_token_purchases > 0 && currentLap?.places_left > 0)
        || (currentLap?.max_purchases_per_user > 0 && currentLap?.places_left > 0);

    const buyHandler = () => {
        if (canBuy) {
            dispatch(setMatrixBuyModalStatus({status: true, lap: currentLap}));
        } else {
            toast(t('На данный момент вы не можете купить токены!'));
        }
    };

    const sellHandler = () => {
        if (currentLapActive) {
            toast(t('На данный момент вы не можете продать токены!'));
        } else {
            dispatch(setMatrixSellModalStatus(true));
        }
    };

    // Десктоп: карусель подстраивается под высоту экрана, чтобы страница влезала без прокрутки.
    // Естественная высота карусели — 39rem (31rem круг + отступы); уменьшаем через zoom, но не меньше 0.6.
    const roundsRef = useRef(null);
    const paginationRef = useRef(null);
    const [carouselZoom, setCarouselZoom] = useState(1);

    useLayoutEffect(() => {
        const block = roundsRef.current;
        if (!block || typeof ResizeObserver === 'undefined') return;
        const update = () => {
            if (window.innerWidth < 1020) { setCarouselZoom(1); return; }
            const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 10;
            const natural = 39 * rem;
            const pagination = paginationRef.current ? paginationRef.current.offsetHeight + 1.6 * rem : 0;
            const available = block.clientHeight - pagination;
            const z = Math.max(0.6, Math.min(1, available / natural));
            setCarouselZoom(Math.round(z * 100) / 100);
        };
        update();
        const ro = new ResizeObserver(update);
        ro.observe(block);
        window.addEventListener('resize', update);
        return () => { ro.disconnect(); window.removeEventListener('resize', update); };
    }, [laps.length]);

    const go = (dir) => setSelected(i => Math.max(0, Math.min(laps.length - 1, i + dir)));

    // свайп на мобилке
    const touchX = useRef(null);
    const onTouchStart = e => { touchX.current = e.touches[0].clientX; };
    const onTouchEnd = e => {
        if (touchX.current === null) return;
        const dx = e.changedTouches[0].clientX - touchX.current;
        if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
        touchX.current = null;
    };

    const lapProps = (lap, featured) => {
        const places = Number(lap?.places) || 0;
        const sold = places - (Number(lap?.places_left) || 0);
        const rate = Number(lap?.token_rate) || 0;
        return {
            state: STATE_BY_STATUS[lap?.status] || 'upcoming',
            featured,
            title: `${t('Круг')} #${lap?.order}`,
            subtitle: lap?.name,
            percent: places > 0 ? (sold / places) * 100 : 0,
            raised: usd(sold * rate),
            goal: usd(places * rate),
        };
    };

    const selectedLap = laps[selected];
    const tokens = Number(userData?.user?.tokens) || 0;
    const usdt = tokens * (Number(userData?.token_rate) || 0);
    const referralEarnings = referralsMain?.received !== undefined && referralsMain?.received !== null
        ? formatNumber(referralsMain.received) : '—';

    const openLink = (url) => {
        if (url) window.open(url, '_blank', 'noopener,noreferrer');
        else toast(t('Скоро'));
    };

    const roundStats = selectedLap ? [
        {
            key: 'price',
            icon: priceTagIcon,
            label: t('Цена раунда'),
            value: `$${new Intl.NumberFormat('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 4}).format(Number(selectedLap.token_rate) || 0)}`,
            secondaryMobile: t('за FTF'),
            onClick: () => navigate('/roadmap'),
        },
        {
            key: 'total',
            icon: coinsIcon,
            label: t('Всего токенов'),
            labelDesktop: `${t('Всего токенов')} (FTF)`,
            value: formatNumber(selectedLap.places, 0),
            unitMobile: 'FTF',
            onClick: () => navigate('/roadmap'),
        },
    ] : [];

    const ticketsAvailable = userData?.user?.available_token_purchases > 0
        ? userData.user.available_token_purchases
        : currentLap?.max_purchases_per_user || 0;

    const userStats = [
        {
            // тикеты = сколько токенов пользователь ещё может купить на текущем круге
            // (та же логика, что «Максимум токенов на круге» в окне покупки)
            key: 'tickets',
            icon: ticketIcon,
            label: t('Тикеты'),
            value: formatNumber(ticketsAvailable, 0),
            onClick: buyHandler,
        },
        {
            key: 'ref',
            icon: usersIcon,
            label: t('Реферальный доход'),
            value: referralEarnings,
            unit: 'FTF',
            onClick: () => navigate('/referral-program'),
        },
    ];

    return (
        <section className={Styles.container}>
            {/* ===== Hero ===== */}
            <div className={Styles.hero}>
                <div className={Styles.heroArt}>
                    <img className={Styles.desktopOnly} src={heroArt} alt=""/>
                    <img className={Styles.mobileOnly} src={heroArtMobile} alt=""/>
                </div>
                <div className={Styles.logo}>CFP</div>
                <p className={`${Styles.heroText} ${Styles.desktopOnly}`}>
                    {t('Crypto for People — это сердце нашей экосистемы, созданное для справедливого распределения ценности между людьми, а не фондами и инсайдерами. Ты точно знаешь, за что платишь: прозрачная цена, понятные этапы и честная экономика, в которой ценность формируется действиями сообщества')}.
                </p>
                <p className={`${Styles.heroText} ${Styles.mobileOnly}`}>
                    {t('Crypto for People — это сердце нашей экосистемы, созданное для справедливого распределения ценности между людьми, а не фондами и инсайдерами')}.
                </p>

                <div className={`${Styles.heroActions} ${Styles.desktopOnly}`}>
                    <BrushButton variant="primary" disabled={!canBuy} onClick={buyHandler}>{t('Купить токены')}</BrushButton>
                    <BrushButton variant="positive" disabled={currentLapActive} onClick={sellHandler}>{t('Продать токены')}</BrushButton>
                    <button type="button" className={Styles.iconBtn} onClick={() => openLink(WHITEPAPER_URL)} aria-label="Whitepaper">
                        <img src={whitepaperBtn} alt="" width="44" height="44"/>
                    </button>
                    <button type="button" className={Styles.iconBtn} onClick={() => openLink(SITE_URL)} aria-label={t('Сайт')}>
                        <img src={openSiteBtn} alt="" width="44" height="44"/>
                    </button>
                </div>

                <div className={`${Styles.heroLinks} ${Styles.mobileOnly}`}>
                    <button type="button" className={Styles.linkBtn} onClick={() => openLink(WHITEPAPER_URL)}>
                        <img src={documentIcon} alt="" width="18" height="18"/>
                        <span>Whitepaper</span>
                    </button>
                    <button type="button" className={Styles.linkBtn} onClick={() => openLink(SITE_URL)}>
                        <img src={externalIcon} alt="" width="18" height="18"/>
                        <span>{t('Сайт')}</span>
                    </button>
                </div>
            </div>

            {/* ===== Баланс (мобилка) ===== */}
            <div className={`${Styles.balanceCard} ${Styles.mobileOnly}`}>
                <div className={Styles.balanceLabel}>{t('Ваш баланс токенов')}</div>
                <div className={Styles.balanceAmount}>
                    <span>{formatNumber(tokens)}</span>
                    <span className={Styles.red}>FTF</span>
                </div>
                <div className={Styles.balanceUsdt}>≈ {formatNumber(usdt)} USDT</div>
                <div className={Styles.balanceActions}>
                    <BrushButton variant="primary" disabled={!canBuy} onClick={buyHandler}>{t('Купить токены')}</BrushButton>
                    <BrushButton variant="positive" disabled={currentLapActive} onClick={sellHandler}>{t('Продать токены')}</BrushButton>
                </div>
            </div>

            {/* ===== Карусель кругов ===== */}
            <div className={Styles.roundsBlock} ref={roundsRef}>
                <div className={`${Styles.sectionTitle} ${Styles.mobileOnly}`}>{t('Инвестиционные раунды')}</div>
                {laps.length ? (
                    <>
                        <div className={Styles.carousel} style={carouselZoom < 1 ? {zoom: carouselZoom} : undefined} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
                            <div className={Styles.track}>
                                {[selected - 1, selected, selected + 1].map((i, pos) => (
                                    laps[i] ? (
                                        <RoundCard
                                            key={laps[i]?.id ?? laps[i]?.order ?? i}
                                            {...lapProps(laps[i], pos === 1)}
                                            onJoin={buyHandler}
                                            joinDisabled={!canBuy || i !== focusIndex}
                                            onClick={pos === 1 ? undefined : () => setSelected(i)}
                                        />
                                    ) : (
                                        <div key={`empty-${pos}`} className={Styles.emptySlot}/>
                                    )
                                ))}
                            </div>
                            {selected > 0 ? (
                                <button type="button" className={`${Styles.arrow} ${Styles.arrowPrev}`} onClick={() => go(-1)} aria-label="prev">
                                    <img className={Styles.desktopOnly} src={prevBtn} alt="" width="44" height="44"/>
                                    <img className={Styles.mobileOnly} src={prevBtnMob} alt="" width="36" height="36"/>
                                </button>
                            ) : null}
                            {selected < laps.length - 1 ? (
                                <button type="button" className={`${Styles.arrow} ${Styles.arrowNext}`} onClick={() => go(1)} aria-label="next">
                                    <img className={Styles.desktopOnly} src={nextBtn} alt="" width="44" height="44"/>
                                    <img className={Styles.mobileOnly} src={nextBtnMob} alt="" width="36" height="36"/>
                                </button>
                            ) : null}
                        </div>
                        <div className={Styles.pagination} ref={paginationRef}>
                            {laps.map((lap, i) => (
                                <i
                                    key={lap?.id ?? i}
                                    className={i === selected ? Styles.dotActive : ''}
                                    onClick={() => setSelected(i)}
                                />
                            ))}
                        </div>
                    </>
                ) : null}
            </div>

            {/* ===== Статы: десктоп — ряд из 4 карточек ===== */}
            <div className={`${Styles.statsRow} ${Styles.desktopOnly}`}>
                {[...roundStats, ...userStats].map(s => (
                    <StatCard key={s.key} icon={s.icon} label={s.labelDesktop || s.label} value={s.value} unit={s.unit} onClick={s.onClick}/>
                ))}
            </div>

            {/* ===== Статы: мобилка — списки ===== */}
            {selectedLap ? (
                <div className={`${Styles.statsList} ${Styles.mobileOnly}`}>
                    <div className={Styles.sectionTitle}>{t('Круг')} #{selectedLap.order}</div>
                    {roundStats.map(s => (
                        <StatCard key={s.key} icon={s.icon} label={s.label} value={s.value} unit={s.unitMobile} unitAccent secondary={s.secondaryMobile} onClick={s.onClick}/>
                    ))}
                </div>
            ) : null}
            <div className={`${Styles.statsList} ${Styles.mobileOnly}`}>
                <div className={Styles.sectionTitle}>{t('Ваша статистика')}</div>
                {userStats.map(s => (
                    <StatCard key={s.key} icon={s.icon} label={s.label} value={s.value} unit={s.unit} onClick={s.onClick}/>
                ))}
            </div>

            <div className={`${Styles.promo} ${Styles.mobileOnly}`}>
                <CommunityPromo/>
            </div>
        </section>
    );
};

export default CabinetMatrixMain;
